import { useEffect, useMemo } from 'react';
import React from 'react';
import { SaveOutlined } from '@ant-design/icons';
import Button from '@client/components/Button';
import { useNavigationBlock } from '@client/lib/navigationBlock';

// Hook used for save buttons around the app - this replaces the autosave functionality found all over
// Expand the functionality as needed
export const useSaveButton = ({
	title,
	visible,
	onSave,
	loading,
	hasPendingChanges,
	blockNavigation,
	testId,
	pulse = true,
	disabled,
	disabledTooltip,
}: {
	onSave: () => void,
	loading: boolean,
	hasPendingChanges: boolean,
	// Defaults to hasPendingChanges - set this when the button is kept enabled for reasons
	// other than unsaved changes, so navigation isn't blocked without cause
	blockNavigation?: boolean,
	title?: string,
	visible?: boolean,
	testId?: string;
	pulse?: boolean;
	disabled?: boolean;
	disabledTooltip?: string;
}) => {
	const {
		useBlocker,
	} = useNavigationBlock();

	useBlocker(blockNavigation ?? hasPendingChanges);

	const button = useMemo(() => {
		if (visible === false) {
			return null;
		}

		return (
			<Button
				loading={loading}
				onClick={onSave}
				disabled={disabled || loading || !hasPendingChanges}
				type="primary"
				icon={(<SaveOutlined />)}
				pulse={pulse && hasPendingChanges && !loading && !disabled}
				data-testid={testId ?? 'save-btn'}
				disabledTooltip={disabledTooltip}
			>
				{title ?? 'Save'}
			</Button>
		);
	}, [hasPendingChanges, onSave, loading, visible, disabled, disabledTooltip]);

	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			// macOS: metaKey = ⌘ , Windows: ctrlKey = Ctrl
			if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
				e.preventDefault(); // prevent browser save dialog
				if (!loading && hasPendingChanges) {
					onSave();
				}
			}
		};

		document.addEventListener('keydown', handleKeyDown);

		return () => {
			document.removeEventListener('keydown', handleKeyDown);
		};
	}, [onSave, loading, hasPendingChanges]);

	return button;
};
